import type { ComponentProps } from "react";
import { useMemo } from "react";

import type { SearchResultsTable } from "app/pages/sales-intelligence/common-components/search-results-table";
import ContactsLockedHook from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsLockedHook/ContactsLockedHook";
import trackingService from "app/pages/sales-intelligence/sub-modules/contacts-search/services/tracking/trackingService";
import type { WithKey } from "app/pages/sales-intelligence/types/withKey";

import withContactSearchErrorBoundary from "../../hoc/withContactSearchErrorBoundary";
import type { ContactSearchEntry } from "../../types/common";

import useCreateContactsResultsTableColumns from "./hooks/useCreateContactsResultsTableColumns";
import { StyledSearchResultsTable } from "./styles";
import type { ContactsResultsTableProps } from "./types";

type TableProps = ComponentProps<typeof SearchResultsTable<ContactSearchEntry>>;

const ContactsResultsTable = (props: ContactsResultsTableProps) => {
    const {
        isShowCounter,
        availableColumns,
        currentPage,
        pageSize,
        totalCount,
        isLoading,
        data,
        virtual,
        scroll,
        onScroll,
        selectedRowKeys,
        isTableRowsSelectionEnabled,
        onEntrySelect,
        isContactsFeatureLocked,

        onSortingChange,
        onPageChange,
        onExportItemToExel,
        onExportItemToCRM,

        onRefetchData,

        createColumnsConfigFactory,
    } = props;

    const columns = useCreateContactsResultsTableColumns(
        {
            onExportItemToExel,
            onExportItemToCRM,
            totalCount: isShowCounter ? totalCount : undefined,
            onRefetchData: onRefetchData,
        },
        createColumnsConfigFactory,
        availableColumns,
    );

    const handleSortChange: TableProps["onSort"] = ({ order, columnKey }): void => {
        trackingService.trackSortColumn(columnKey as string);
        onSortingChange(columnKey as string, order === "ascend");
    };

    const selectionProps: TableProps["selection"] = useMemo(
        () => ({
            columnWidth: 48,
            enabled: isTableRowsSelectionEnabled,
            selectedRowKeys,
            onChange(_keys, rows: WithKey<ContactSearchEntry>[], _info) {
                onEntrySelect(rows);
            },
        }),
        [isTableRowsSelectionEnabled, selectedRowKeys, onEntrySelect],
    );

    const paginationProps: TableProps["pagination"] = useMemo(
        () => ({
            pageSize,
            total: totalCount,
            current: currentPage,
            onChange(_currentPage: number, _pageSize: number, nextPage) {
                trackingService.trackPageNavigation(nextPage > currentPage ? "forward" : "backward");
                onPageChange(nextPage);
            },
        }),
        [totalCount, currentPage, pageSize],
    );

    const noData = useMemo(
        () => ({
            Component: () => (
                <ContactsLockedHook
                    title="si.contacts.locked-state.title"
                    subTitle="si.contacts.locked-state.subtitle"
                />
            ),
        }),
        [],
    );

    return (
        <StyledSearchResultsTable
            isLoading={isLoading}
            data={!isContactsFeatureLocked ? data : []}
            noData={isContactsFeatureLocked ? noData : undefined}
            columnConfig={columns}
            rowSelection={isTableRowsSelectionEnabled ? selectionProps : undefined}
            pagination={!isContactsFeatureLocked ? paginationProps : undefined}
            virtual={virtual}
            scroll={scroll}
            onScroll={onScroll}
            onSort={handleSortChange}
        />
    );
};

export default withContactSearchErrorBoundary(ContactsResultsTable);
